import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ScrollText, Plus, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function RulesPage() {
  const [regras, setRegras] = useState([]);
  const [texto, setTexto] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/regras");
      setRegras(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    const t = texto.trim();
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await api.post("/regras", { texto: t });
      setRegras((r) => [data, ...r]);
      setTexto("");
      toast.success("Regra adicionada");
    } catch (err) {
      toast.error("Não foi possível adicionar");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/regras/${id}`);
    setRegras((r) => r.filter((x) => x.id !== id));
    toast.success("Regra removida");
  };

  return (
    <div className="h-full overflow-y-auto px-4 lg:px-8 py-8 pl-14 lg:pl-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center">
            <ScrollText size={20} className="text-emerald-400" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Regras permanentes</h1>
            <p className="text-sm text-slate-400">
              Instruções que entram no system prompt de todas as conversas, seja qual for o modelo.
            </p>
          </div>
        </div>

        <form onSubmit={add} className="mt-6 flex gap-2">
          <input
            data-testid="rule-input-text"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Ex.: Responde sempre em português de Portugal."
            className="flex-1 px-4 py-2.5 rounded-lg bg-slate-900/70 border border-slate-800 focus:border-emerald-500/60 focus:outline-none text-sm"
          />
          <button
            data-testid="add-rule-button"
            disabled={busy || !texto.trim()}
            className="px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Adicionar
          </button>
        </form>

        <div className="mt-6 space-y-2">
          {loading && <div className="text-slate-500 text-sm font-mono">a carregar…</div>}
          {!loading && regras.length === 0 && (
            <div className="text-center py-14 border border-dashed border-slate-800 rounded-2xl">
              <p className="text-slate-500 text-sm">Ainda não tens regras. Adiciona a primeira acima.</p>
            </div>
          )}
          {regras.map((r) => (
            <div
              key={r.id}
              data-testid="rule-item"
              className="group flex items-start gap-3 p-4 rounded-xl bg-slate-900/60 border border-slate-800 hover:border-slate-700 transition-colors"
            >
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
              <p className="flex-1 text-sm text-slate-200 leading-relaxed">{r.texto}</p>
              <button
                data-testid="rule-delete-button"
                onClick={() => remove(r.id)}
                className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-all"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
